import React, { useEffect } from 'react';
import { useCookies } from 'next-client-cookies';
import { Money } from 'shared/types/product';
import AlertBanner from 'components/commercetools-ui/molecules/alert-banner';
import { useAddToCartOverlay } from 'context/add-to-cart-overlay';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { MaxCartValueText } from './types';

type Props = {
  maxCartValueText: MaxCartValueText;
  cartSum: Money;
  commonGridLayoutClassname?: string;
};

/**
 * A component that displays a alert banner when the user's cart total exceeds a predefined maximum threshold.
 */
const MaximumCartValueAlert: React.FC<Props> = ({ maxCartValueText, cartSum, commonGridLayoutClassname }) => {
  const cookies = useCookies();
    const locale = cookies.get('locale') as string;
  const { setShouldDisplayMaxCartValueAlert } = useAddToCartOverlay();

  const { maxCartAlertHeading, maxCartAlertLine1, maxCartAlertLine2, maxCartValue } = maxCartValueText[0];

  // This will localized the cart value according to the currency code. If currency code not passed then default will be USD.
  // E.g., $100, €200
  const localizedMaxCartValue = CurrencyHelpers.localizedFormatForCurreny(maxCartValue, locale, cartSum.currencyCode);

  const { centAmount, fractionDigits = 0 } = cartSum || {};
  const totalCartValue = centAmount ? (centAmount / Math.pow(10, fractionDigits)).toFixed(fractionDigits) : 0;

  const shouldDisplayAlert = Number(totalCartValue) > Number(maxCartValue);

  // Syncs the alert visibility state to AddToCartOverlay provider to trigger cross-component side effects.
  useEffect(() => {
    setShouldDisplayMaxCartValueAlert(shouldDisplayAlert);
  }, [shouldDisplayAlert, setShouldDisplayMaxCartValueAlert]);

  if (!shouldDisplayAlert) return null;

  return (
    <div className={`${commonGridLayoutClassname} grid-rows-1 pt-8`}>
      <div className="col-start-1 col-end-6 lg:col-start-2 lg:row-end-4">
        <AlertBanner>
          <div className="text-sm font-bold" data-ffid="lbl-max-cart-alert-heading">{maxCartAlertHeading}</div>
          <div className="text-sm" data-ffid="lbl-max-cart-alert-description">
            {maxCartAlertLine1} {localizedMaxCartValue}
            {'.'} {maxCartAlertLine2}
          </div>
        </AlertBanner>
      </div>
    </div>
  );
};

export default MaximumCartValueAlert;
